import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Video, MessageSquare, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCmsContent } from '../hooks/useCmsContent';
import { parseHighlight } from '../utils/richText';

const TRIAL_IMAGE_FB = "/assets/photos/site-updates/hero-final-touches.webp";

const DEFAULT_TRIAL_INCLUDES = [
  { icon: MessageSquare, title: 'Needs Assessment',      description: 'We discuss your goals, interests, and what you want to achieve with English.',  color: 'bg-[#D72B2B]' },
  { icon: CheckCircle,   title: 'Level Evaluation',      description: 'A friendly conversation to understand your current English level.',              color: 'bg-[#4CAF50]' },
  { icon: Calendar,      title: 'Personalized Plan',     description: 'Receive a recommended learning path tailored to your needs.',                   color: 'bg-[#F5A623]' },
  { icon: Video,         title: 'Teaching Style Preview', description: 'Experience our engaging and supportive teaching approach.',                     color: 'bg-[#1A2E6E]' }
];

const DEFAULT_STEPS = [
  { step: '1', title: 'Receive Your Assessment',    description: "You'll get feedback on your current level and areas for improvement.", color: 'bg-[#D72B2B]' },
  { step: '2', title: 'Get Your Recommended Plan',  description: "We'll suggest the best course and package based on your goals.",        color: 'bg-[#4CAF50]' },
  { step: '3', title: 'Choose Your Package',         description: 'Select the lesson package that fits your schedule and budget.',        color: 'bg-[#F5A623]' },
  { step: '4', title: 'Start Your Journey',          description: 'Begin regular lessons and track your progress towards confident English.', color: 'bg-[#1A2E6E]' }
];

export const BookTrial = () => {
  const { settings } = usePublicSettings();
  const { t, tList } = useLanguage();
  const cms = useCmsContent('booktrial');
  const trialBookingUrl = settings.trial_booking_url?.trim();
  const hasBookingUrl = Boolean(trialBookingUrl);

  const heroBadge    = cms.get('hero', 'badge',  t('bookTrial.hero.badge'));
  const heroTitle    = cms.get('hero', 'title',  t('bookTrial.hero.title'));
  const heroBody     = cms.get('hero', 'body',   t('bookTrial.hero.body'));
  const heroImage    = cms.get('hero', 'image',  TRIAL_IMAGE_FB);
  const includesBadge = cms.get('includes', 'badge', t('bookTrial.includesSection.badge'));
  const includesTitle = cms.get('includes', 'title', t('bookTrial.includesSection.title'));
  const includesBody  = cms.get('includes', 'body',  t('bookTrial.includesSection.body'));
  const nextBadge    = cms.get('next_steps', 'badge', t('bookTrial.nextStepsSection.badge'));
  const nextTitle    = cms.get('next_steps', 'title', t('bookTrial.nextStepsSection.title'));
  const bottomBody   = cms.get('bottom_cta', 'body',  t('bookTrial.bottomCta.body'));
  const bottomBtn    = cms.get('bottom_cta', 'btn',   t('bookTrial.bottomCta.button'));

  const localizedIncludes = tList('bookTrial.includesList');
  const cmsIncludes = cms.getList('trial_includes', []);
  const trialIncludes = DEFAULT_TRIAL_INCLUDES.map((item, i) => ({
    ...item,
    title:       cmsIncludes[i]?.title       || localizedIncludes[i]?.title       || item.title,
    description: cmsIncludes[i]?.description || localizedIncludes[i]?.description || item.description,
  }));

  const localizedSteps = tList('bookTrial.stepsList');
  const cmsSteps = cms.getList('what_happens', []);
  const steps = DEFAULT_STEPS.map((item, i) => ({
    ...item,
    title:       cmsSteps[i]?.title       || localizedSteps[i]?.title       || item.title,
    description: cmsSteps[i]?.description || localizedSteps[i]?.description || item.description,
  }));

  return (
    <main className="pt-24" data-testid="book-trial-page">
      {/* Hero Section */}
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="badge-red mb-4 inline-block">
                {heroBadge}
              </span>
              <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(heroTitle)}
              </h1>
              <p className="text-lg text-[#1A2E6E]/70 leading-relaxed mb-8">
                {heroBody}
              </p>
              <div className="flex flex-col gap-3 text-sm text-[#1A2E6E]/70 sm:flex-row sm:items-center sm:gap-4">
                <span className="flex items-center gap-2">
                  <Clock size={16} className="text-[#4CAF50]" />
                  {t('bookTrial.durationBadge')}
                </span>
                <span className="flex items-center gap-2">
                  <Video size={16} className="text-[#1A2E6E]" />
                  {t('bookTrial.viaGoogleMeet')}
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="photo-container h-64 sm:h-80"
            >
              <img
                src={heroImage}
                alt="English teacher during online lesson"
                className="photo-fit-card"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-green mb-4 inline-block">{includesBadge}</span>
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {parseHighlight(includesTitle)}
            </h2>
            <p className="text-[#1A2E6E]/70 max-w-2xl mx-auto">
              {includesBody}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {trialIncludes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-5 sm:p-6 text-center card-accent-hover card-accent-navy"
              >
                <div className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <item.icon size={28} className="text-white" />
                </div>
                <h3 className="font-sora font-semibold text-lg text-[#1A2E6E] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#1A2E6E]/70 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Booking Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="glass-card p-6 sm:p-8 md:p-12 text-center border-t-4 border-t-[#D72B2B]">
              <div className="w-16 h-16 rounded-2xl bg-[#D72B2B] flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Calendar size={32} className="text-white" />
              </div>
              <h3 className="font-sora font-bold text-2xl text-[#1A2E6E] mb-4">
                {t('bookTrial.scheduleTitle')}
              </h3>
              <p className="text-[#1A2E6E]/70 mb-6">
                {hasBookingUrl
                  ? t('bookTrial.scheduleBodyWithUrl')
                  : t('bookTrial.scheduleBodyWithoutUrl')}
              </p>

              {hasBookingUrl ? (
                <a
                  href={trialBookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex w-full items-center justify-center gap-2 px-8 py-4 text-base sm:w-auto sm:px-10 sm:text-lg"
                  data-testid="schedule-trial-btn"
                >
                  {t('bookTrial.openCalendarButton')} <ArrowRight size={20} />
                </a>
              ) : (
                <Link
                  to="/contact"
                  className="btn-primary inline-flex w-full items-center justify-center gap-2 px-8 py-4 text-base sm:w-auto sm:px-10 sm:text-lg"
                  data-testid="schedule-trial-btn"
                >
                  {t('bookTrial.contactToScheduleButton')} <ArrowRight size={20} />
                </Link>
              )}

              <p className="mt-6 text-sm text-[#1A2E6E]/60">
                {hasBookingUrl
                  ? t('bookTrial.scheduleNoteWithUrl')
                  : t('bookTrial.scheduleNoteWithoutUrl')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="section-padding bg-paper-lines">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-orange mb-4 inline-block">{nextBadge}</span>
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {parseHighlight(nextTitle)}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {steps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-3 sm:gap-4"
              >
                <div className={`w-10 h-10 rounded-full ${item.color} text-white flex items-center justify-center font-bold flex-shrink-0 shadow-md`}>
                  {item.step}
                </div>
                <div className="glass-card p-5 sm:p-6 flex-grow">
                  <h3 className="font-sora font-semibold text-lg text-[#1A2E6E] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-[#1A2E6E]/70">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-watercolor-soft">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#4CAF50]/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles size={28} className="text-[#4CAF50]" />
            </div>
            <p className="text-[#1A2E6E]/70 mb-6">
              {bottomBody}
            </p>
            <Link
              to="/level-test"
              className="btn-green inline-flex w-full items-center justify-center gap-2 sm:w-auto"
              data-testid="trial-level-test-btn"
            >
              {bottomBtn} <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
